import { notificationNavigation } from '../../../utilities/notifications/notificationNavigation';
import React from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import EventNotification from "../../../interfaces/eventNotification/EventNotification";
import { getTimeAgo } from "../../../utilities/dateUtils";
import {
  renderNotificationMessage,
  renderNotificationTitle,
} from "../../../utilities/notifications/notificationUtils";
import {
  getNotificationCategoryLabel,
  NotificationCategoryIcon,
} from "../../../utilities/notifications/notificationPresentation";
import MeetingInvitationActions from "./MeetingInvitationActions";

interface ToastNotificationProps {
  notification: EventNotification;
  groupCount?: number;
  onClose?: () => void;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({
  notification,
  groupCount = 1,
  onClose,
}) => {
  const navigate = useNavigate();
  const isMeetingInvitation = notification.type.toLowerCase() === "meeting.invitation";

  const primaryResource =
    notification.notificationResources?.find(
      (resource) => resource.resourceType === "task_action",
    ) ??
    notification.notificationResources?.find(
      (resource) => resource.resourceType === "task",
    ) ??
    notification.notificationResources?.find(
      (resource) =>
        resource.resourceType !== "user" && Boolean(resource.resourceId),
    );

  const meetingResource = notification.notificationResources?.find(
    (resource) => resource.resourceType.toLowerCase() === "meeting",
  );

  const isInteractiveTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;

    return Boolean(
      target.closest(
        "button, a, input, select, textarea, [role='button'], [data-toast-interactive='true']",
      ),
    );
  };

  const handleClick = (event?: React.MouseEvent<HTMLDivElement>) => {
    // Links, the close button, and any future interactive controls own their
    // clicks. The surrounding toast only navigates for non-interactive areas.
    if (event && isInteractiveTarget(event.target)) return;
    if (!primaryResource?.resourceId) return;

    navigate(notificationNavigation(primaryResource.resourceId));
    onClose?.();
  };

  return (
    <div
      className={[
        "waivera-toast-content relative flex min-w-0 flex-1 items-start gap-3 overflow-hidden px-0.5 py-0.5",
        primaryResource?.resourceId ? "cursor-pointer" : "",
      ].join(" ")}
      onClick={handleClick}
      role={primaryResource?.resourceId ? "button" : undefined}
      tabIndex={primaryResource?.resourceId ? 0 : undefined}
      onKeyDown={(event) => {
        if (
          primaryResource?.resourceId &&
          (event.key === "Enter" || event.key === " ")
        ) {
          event.preventDefault();
          handleClick();
        }
      }}
    >
      <span
        className="absolute -left-3 top-0 h-full w-[3px] rounded-r-full bg-[#7E57C2]"
        aria-hidden="true"
      />

      <NotificationCategoryIcon notification={notification} />

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="line-clamp-1 text-[13px] font-semibold leading-snug text-gray-900 dark:text-zinc-100">
              {renderNotificationTitle(notification)}
            </div>

            <div className="mt-1 line-clamp-2 text-[12px] font-normal leading-relaxed text-gray-600 dark:text-zinc-300 [&_a]:font-medium [&_a]:text-[#6D4DB3] dark:[&_a]:text-purple-300 [&_a]:no-underline">
              {renderNotificationMessage(notification)}
            </div>
          </div>

          <button
            type="button"
            className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-400 dark:text-zinc-500 transition-colors hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-700 dark:hover:text-zinc-200"
            data-toast-interactive="true"
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClose?.();
            }}
            aria-label="Close notification"
          >
            <X size={15} />
          </button>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {!isMeetingInvitation && (
            <span className="rounded-md bg-gray-100 dark:bg-zinc-800 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              {getNotificationCategoryLabel(notification)}
            </span>
          )}
          <span className="text-[10px] font-medium text-gray-400 dark:text-zinc-500">
            {getTimeAgo(notification.createdAt)}
          </span>
          {!isMeetingInvitation && meetingResource?.resourceId && (
            <button
              type="button"
              className="text-[10px] font-semibold text-[#6D4DB3] hover:underline dark:text-purple-300"
              onClick={() => {
                navigate(notificationNavigation(meetingResource.resourceId));
                onClose?.();
              }}
            >
              View meeting
            </button>
          )}
          {groupCount > 1 && (
            <span className="text-[10px] font-semibold text-[#6D4DB3]">
              {groupCount} related updates
            </span>
          )}
        </div>

        <MeetingInvitationActions
          notification={notification}
          compact
          onNavigate={onClose}
        />
      </div>
    </div>
  );
};

export default ToastNotification;
